import { useEffect } from 'react'
import { useSelector, useStore } from 'react-redux'
import { getForumSocketToken } from '../../api/api'
import { forumUnreadAPI } from '../../api/forum-unread'
import { RootState, useAppDispatch } from '../../store/store'
import { forumUnreadActions, messagesUnreadActions } from '../../store/slices/forumUnreadSlice'

let sequence = 0

export const useForumUnread = (competenceId?: string, personal = false) => {
  const actions = personal ? messagesUnreadActions : forumUnreadActions
  const stateKey = personal ? 'messagesUnread' : 'forumUnread'
  const userId = useSelector((state: RootState) => state.account.user?.id)
  const dispatch = useAppDispatch()
  const store = useStore<RootState>()
  useEffect(() => {
    if (!userId || !competenceId) return
    const competence = Number(competenceId)
    const scope = `${userId}:${competence}:${++sequence}`
    dispatch(actions.activate({ scope, userId, competenceId: competence }))
    let stopped = false
    let busy = false
    let dirty = false
    let socket: WebSocket | undefined
    let attempts = 0
    let authRetry = false
    let retry: ReturnType<typeof setTimeout> | undefined
    let batch: ReturnType<typeof setTimeout> | undefined
    let snapshotRetry: ReturnType<typeof setTimeout> | undefined
    const stop = () => {
      stopped = true
      socket?.close()
      dispatch(actions.release(scope))
    }
    const refresh = async () => {
      if (stopped) return
      if (busy) {
        dirty = true
        return
      }
      busy = true
      dirty = false
      if (snapshotRetry) clearTimeout(snapshotRetry)
      snapshotRetry = undefined
      const revision = store.getState()[stateKey].revision
      try {
        const snapshot = await (personal
          ? forumUnreadAPI.snapshot(competence, true)
          : forumUnreadAPI.snapshot(competence))
        if (!stopped) dispatch(actions.receive({ scope, revision, snapshot }))
      } catch (error) {
        const status = (error as { response?: { status?: number } }).response?.status
        if (status === 401 || status === 403) stop()
        else if (!stopped) snapshotRetry = setTimeout(refresh, 5000)
      } finally {
        busy = false
        if (dirty && !stopped) schedule()
      }
    }
    const schedule = () => {
      if (stopped || batch) return
      batch = setTimeout(() => {
        batch = undefined
        refresh()
      }, 100)
    }
    let revision = store.getState()[stateKey].revision
    let forumRevision = store.getState().forumUnread.revision
    const unsubscribe = store.subscribe(() => {
      const state = store.getState()
      if (state[stateKey].scope !== scope || state.account.user?.id !== userId) {
        stopped = true
        socket?.close()
        return
      }
      if (personal && forumRevision !== state.forumUnread.revision) {
        forumRevision = state.forumUnread.revision
        dispatch(actions.invalidate())
        return
      }
      if (revision !== state[stateKey].revision) {
        revision = state[stateKey].revision
        dirty = true
        schedule()
      }
    })
    const invalidate = () => {
      if (!stopped) dispatch(actions.invalidate())
    }
    const connect = async () => {
      try {
        const token = await getForumSocketToken()
        if (stopped) return
        if (!token) return stop()
        const url = new URL(process.env.REACT_APP_ENDPOINT || window.location.origin, window.location.origin)
        url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
        url.pathname = `/ws/forum/unread/${competence}/`
        url.search = new URLSearchParams({ token }).toString()
        socket = new WebSocket(url.toString())
        socket.onmessage = ({ data }) => {
          try {
            const event = JSON.parse(data).event
            if (event === 'connection.ready') {
              attempts = 0
              authRetry = false
              invalidate()
            } else if (event === 'unread.changed') invalidate()
          } catch {
            /* A malformed notification cannot change authoritative counts. */
          }
        }
        socket.onclose = ({ code }) => {
          if (stopped) return
          if (code === 4403 || (code === 4401 && authRetry)) return stop()
          if (code === 4401) authRetry = true
          retry = setTimeout(connect, Math.min(30000, 1000 * Math.pow(2, attempts++)))
        }
      } catch (error) {
        if (stopped) return
        const status = (error as { response?: { status?: number } }).response?.status
        if (status === 401 || status === 403) stop()
        else retry = setTimeout(connect, Math.min(30000, 1000 * Math.pow(2, attempts++)))
      }
    }
    const onVisible = () => {
      if (document.visibilityState === 'visible') invalidate()
    }
    window.addEventListener('focus', onVisible)
    document.addEventListener('visibilitychange', onVisible)
    refresh()
    if (typeof WebSocket !== 'undefined') connect()
    return () => {
      stopped = true
      unsubscribe()
      if (batch) clearTimeout(batch)
      if (retry) clearTimeout(retry)
      if (snapshotRetry) clearTimeout(snapshotRetry)
      socket?.close()
      window.removeEventListener('focus', onVisible)
      document.removeEventListener('visibilitychange', onVisible)
      dispatch(actions.release(scope))
    }
  }, [competenceId, userId, dispatch, store, personal, actions, stateKey])
}
